import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useMemo, useState } from "react";
import bankingService, {
  BANK_AUTH_STATE_KEY,
  getBankingRedirectUrl,
} from "api/bankingService";
import { IAspsp } from "dto/Banking/IBanking";
import styles from "./styles.module.scss";

/**
 * Nazwa banku mBank w katalogu ASPSP Enable Banking. Bank wyróżniony i domyślny
 * dla integracji Noty; dostępny też jako fallback, zanim BE zacznie zwracać pełną
 * listę z /banking/aspsps.
 */
const MBANK_NAME = "mBank";

const FALLBACK_ASPSPS: IAspsp[] = [{ Name: MBANK_NAME, Country: "PL" }];

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Wybór banku (ASPSP) i rozpoczęcie autoryzacji AIS. Po wyborze BE zwraca adres
 * silnego uwierzytelnienia (SCA), na który przekierowujemy użytkownika. Bank po
 * autoryzacji odsyła go na RedirectUrl (rejestr Operacje finansowe).
 */
const ConnectBankModal = ({ open, onClose }: Props) => {
  const { data, isLoading } = bankingService.useGetAspsps("PL", open);
  const startAuth = bankingService.useStartAuth();
  const [pendingName, setPendingName] = useState<string | null>(null);

  // Lista banków: z BE, a gdy jej (jeszcze) brak — fallback z mBankiem.
  // mBank wypychany na górę jako bank domyślny/rekomendowany.
  const aspsps = useMemo(() => {
    const fromApi = Array.isArray(data) ? data : [];
    const list = fromApi.length > 0 ? fromApi : FALLBACK_ASPSPS;
    return [...list].sort(
      (a, b) =>
        (b.Name === MBANK_NAME ? 1 : 0) - (a.Name === MBANK_NAME ? 1 : 0),
    );
  }, [data]);

  const redirectUrl = useMemo(() => getBankingRedirectUrl(), []);

  const handlePick = (aspsp: IAspsp) => {
    setPendingName(aspsp.Name);
    startAuth.mutate(
      {
        AspspName: aspsp.Name,
        AspspCountry: aspsp.Country,
        RedirectUrl: redirectUrl,
      },
      {
        onSuccess: (res) => {
          sessionStorage.setItem(BANK_AUTH_STATE_KEY, res.State);
          window.location.assign(res.AuthorizationUrl);
        },
        onError: () => setPendingName(null),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>Podłącz rachunek bankowy</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 14 }}>
          Wybierz bank, aby podłączyć istniejący rachunek. Zostaniesz
          przekierowany do logowania w banku i potwierdzenia zgody (PSD2).
        </DialogContentText>
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className={styles.aspspList}>
            {aspsps.map((aspsp) => (
              <button
                key={`${aspsp.Name}-${aspsp.Country}`}
                type="button"
                className={styles.aspspItem}
                disabled={startAuth.isLoading}
                onClick={() => handlePick(aspsp)}
              >
                {aspsp.LogoUrl && <img src={aspsp.LogoUrl} alt="" />}
                <span className={styles.aspspName}>{aspsp.Name}</span>
                {aspsp.Name === MBANK_NAME && (
                  <span className={styles.recommended}>Rekomendowany</span>
                )}
                <span className={styles.aspspCountry}>{aspsp.Country}</span>
                {pendingName === aspsp.Name && startAuth.isLoading && (
                  <CircularProgress size={16} />
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectBankModal;
